import { useMemo } from "react";
import { toast } from "react-toastify";

import { Box } from "@design-system/Box/Box";
import { Button } from "@design-system/Button/Button";
import { Card } from "@design-system/Card/Card";
import { OAuthIntegrationTypeEnum } from "@graphql/generated/graphql.g";
import {
  useCreateMicrosoftOAuthUrlMutation,
  useDisconnectMicrosoftOAuthMutation,
  useOAuthConnectionsQuery,
} from "@graphql/integrations/integrations.g";

export const Integrations = () => {
  const {
    data: oauthConnectionsData,
    isLoading,
    refetch,
  } = useOAuthConnectionsQuery();

  const microsoftConnection = useMemo(() => {
    return oauthConnectionsData?.oauthConnections?.edges?.find(
      (edge) =>
        edge?.node?.connectionType ===
        OAuthIntegrationTypeEnum.MicrosoftPartnerCenter
    )?.node;
  }, [oauthConnectionsData]);

  const isConnected = !!microsoftConnection && microsoftConnection.isActive;

  const createMicrosoftOAuthUrlMutation = useCreateMicrosoftOAuthUrlMutation({
    onSuccess: (data) => {
      const oauthUrl = data.createMicrosoftOauthUrl?.oauthUrl?.url;
      if (oauthUrl) {
        window.location.href = oauthUrl;
      }
    },
    onError: (error) => {
      console.error("Failed to get Microsoft OAuth URL:", error);
      toast.error("Failed to initiate connection. Please try again.");
    },
  });

  const disconnectMicrosoftOAuthMutation =
    useDisconnectMicrosoftOAuthMutation({
      onSuccess: (data) => {
        if (data.disconnectMicrosoftOauth?.errors?.length) {
          const errorMessage =
            data.disconnectMicrosoftOauth.errors
              .map((e) => e?.message)
              .filter(Boolean)
              .join(", ") || "Failed to disconnect";
          toast.error(errorMessage);
          return;
        }

        if (data.disconnectMicrosoftOauth?.success) {
          toast.success("Successfully disconnected from Microsoft Partner Center");
          refetch();
        }
      },
      onError: (error: Error) => {
        console.error("Failed to disconnect Microsoft OAuth:", error);
        toast.error("Failed to disconnect. Please try again.");
      },
    });

  const handleConnect = () => {
    createMicrosoftOAuthUrlMutation.mutate({});
  };

  const handleDisconnect = () => {
    if (!microsoftConnection?.id) {
      toast.error("Connection ID not found");
      return;
    }
    disconnectMicrosoftOAuthMutation.mutate({ id: microsoftConnection.id });
  };

  return (
    <>
      <Card>
        <Card.Header withBottomPadding>
          <Card.Header.Title>Microsoft Partner Center</Card.Header.Title>
        </Card.Header>
        <Card.Body grow>
          <Box direction="column" gap={6}>
            <Box>
              Connect your Microsoft Partner Center account to enable seamless
              integration and management of your services directly from our
              platform.
            </Box>
            <Box justify="flex-end" fullStretch>
              {isConnected ? (
                <Button
                  color="primary"
                  variant="outlined"
                  style={{ marginTop: 16 }}
                  onClick={handleDisconnect}
                  disabled={
                    disconnectMicrosoftOAuthMutation.isPending || isLoading
                  }
                >
                  {disconnectMicrosoftOAuthMutation.isPending
                    ? "Disconnecting..."
                    : "Disconnect Microsoft Partner Center"}
                </Button>
              ) : (
                <Button
                  color="primary"
                  style={{ marginTop: 16 }}
                  onClick={handleConnect}
                  disabled={
                    createMicrosoftOAuthUrlMutation.isPending || isLoading
                  }
                >
                  {createMicrosoftOAuthUrlMutation.isPending
                    ? "Connecting..."
                    : "Connect to Microsoft Partner Center"}
                </Button>
              )}
            </Box>
          </Box>
        </Card.Body>
      </Card>
    </>
  );
};
